import {
  CarbonRating,
  DietaryCategory,
  DietaryAttributeStatus,
  AllergenSource,
} from '../../app/services/api/recipeV2/recipeV2Types';
import {
  RangePlanningItem,
  ItemLifecycleStatus,
} from '../../app/services/api/rangePlanningItems/rangePlanningItemsTypes';
import { NutritionSource } from '../../components/shared/nutrition/types';

/** Seed data for the Range Planning "Items" demo screen (src/pages/RangePlanningItems).
 * recipeUsage entries intentionally reference recipe IDs 102/301/302 which also appear in
 * recipeUsageData.ts, so cross-links between Items <-> Recipes <-> Menus line up in the demo. */
export const rangePlanningItems: RangePlanningItem[] = [
  {
    id: 1,
    itemNumber: 'ITM-1001',
    name: 'Chicken Breast Fillet, 5kg',
    category: 'Meat & Poultry',
    baseUom: 'KG',
    status: ItemLifecycleStatus.Active,
    supplier: {
      supplierName: 'Meadow Farm Poultry',
      supplierCode: 'SUP-204',
      leadTimeDays: 3,
      minimumOrderQuantity: 20,
      alternateSuppliers: ['Coastal Poultry Co.', 'Green Acres Farms'],
    },
    uomConversions: [
      { fromUom: 'CASE', toUom: 'KG', factor: 5 },
      { fromUom: 'KG', toUom: 'PORTION', factor: 6.67 },
    ],
    pricing: {
      currency: 'GBP',
      contractPrice: 8.45,
      spotPrice: 9.1,
      previousPrice: 8.1,
      priceEffectiveDate: '2025-01-01',
    },
    dietaryAttributes: [
      { id: 1, source: AllergenSource.Feed, code: 'GLUTEN', name: 'Gluten', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.FreeFrom },
      { id: 2, source: AllergenSource.Feed, code: 'HALAL', name: 'Halal', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Certified },
    ],
    carbonRating: CarbonRating.C,
    nutrition: {
      source: NutritionSource.Supplier,
      servingSize: '100g',
      per100g: { energyKj: 490, energyKcal: 117, fat: 2.6, saturates: 0.7, carbohydrate: 0, sugars: 0, fibre: 0, protein: 22, salt: 0.15 },
      perServing: {},
    },
    catalogueAvailability: [
      { catalogueName: 'Core Range', available: true },
      { catalogueName: 'Premium Sites', available: true },
      { catalogueName: 'Education Sites', available: false },
    ],
    recipeUsage: [
      { recipeId: 622, recipeName: 'Rosemary Chicken, Wild Mushroom and Leek Fricassee' },
    ],
    inventoryMapping: [
      { warehouseCode: 'WH-SOUTH', binLocation: 'C-04-1', onHandQty: 90, reorderPoint: 120 },
    ],
  },
  {
    id: 2,
    itemNumber: 'ITM-1002',
    name: 'Chicken Breast Fillet, Frozen 5kg',
    category: 'Meat & Poultry',
    baseUom: 'KG',
    status: ItemLifecycleStatus.PendingReview,
    supplier: {
      supplierName: 'Coastal Poultry Co.',
      supplierCode: 'SUP-311',
      leadTimeDays: 5,
      minimumOrderQuantity: 10,
      alternateSuppliers: ['Meadow Farm Poultry'],
    },
    uomConversions: [{ fromUom: 'CASE', toUom: 'KG', factor: 5 }],
    pricing: {
      currency: 'GBP',
      contractPrice: 8.6,
      previousPrice: 8.6,
      priceEffectiveDate: '2024-11-01',
    },
    dietaryAttributes: [
      { id: 3, source: AllergenSource.Feed, code: 'GLUTEN', name: 'Gluten', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.FreeFrom },
    ],
    carbonRating: CarbonRating.D,
    nutrition: {
      source: NutritionSource.Supplier,
      servingSize: '100g',
      per100g: { energyKj: 495, energyKcal: 118, fat: 2.8, saturates: 0.8, carbohydrate: 0, sugars: 0, fibre: 0, protein: 21.8, salt: 0.18 },
      perServing: {},
    },
    catalogueAvailability: [
      { catalogueName: 'Core Range', available: true },
    ],
    recipeUsage: [],
    inventoryMapping: [
      { warehouseCode: 'WH-NORTH', binLocation: 'A-12-4', onHandQty: 40, reorderPoint: 100 },
    ],
  },
  {
    id: 3,
    itemNumber: 'ITM-2050',
    name: 'Basmati Rice, 10kg',
    category: 'Dry Goods',
    baseUom: 'KG',
    status: ItemLifecycleStatus.Active,
    supplier: {
      supplierName: 'Global Grains Ltd',
      supplierCode: 'SUP-118',
      leadTimeDays: 7,
      minimumOrderQuantity: 50,
      alternateSuppliers: ['Harvest Direct'],
    },
    uomConversions: [{ fromUom: 'CASE', toUom: 'KG', factor: 10 }],
    pricing: {
      currency: 'GBP',
      contractPrice: 1.35,
      spotPrice: 1.5,
      previousPrice: 1.2,
      priceEffectiveDate: '2025-02-01',
    },
    dietaryAttributes: [
      { id: 4, source: AllergenSource.Feed, code: 'GLUTEN', name: 'Gluten', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.FreeFrom },
      { id: 5, source: AllergenSource.Feed, code: 'VEGAN', name: 'Vegan', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable },
    ],
    carbonRating: CarbonRating.B,
    nutrition: {
      source: NutritionSource.Supplier,
      servingSize: '100g',
      per100g: { energyKj: 1520, energyKcal: 360, fat: 0.6, saturates: 0.1, carbohydrate: 79, sugars: 0.1, fibre: 1.3, protein: 7.1, salt: 0.01 },
      perServing: {},
    },
    catalogueAvailability: [
      { catalogueName: 'Core Range', available: true },
      { catalogueName: 'Premium Sites', available: true },
      { catalogueName: 'Education Sites', available: true },
    ],
    recipeUsage: [
      { recipeId: 610, recipeName: 'Braised Lamb, Tomato and Apricot Tagine' },
      { recipeId: 611, recipeName: 'Harissa-Roasted Carrot and Chickpea Tagine' },
    ],
    inventoryMapping: [
      { warehouseCode: 'WH-NORTH', binLocation: 'B-02-1', onHandQty: 620, reorderPoint: 300 },
    ],
  },
  {
    id: 4,
    itemNumber: 'ITM-3300',
    name: 'Cheddar Cheese, Block 2.5kg',
    category: 'Dairy',
    baseUom: 'KG',
    status: ItemLifecycleStatus.Active,
    supplier: {
      supplierName: 'Northfield Dairy',
      supplierCode: 'SUP-045',
      leadTimeDays: 2,
      minimumOrderQuantity: 15,
      alternateSuppliers: [],
    },
    uomConversions: [{ fromUom: 'CASE', toUom: 'KG', factor: 2.5 }],
    pricing: {
      currency: 'GBP',
      contractPrice: 6.2,
      spotPrice: 7.05,
      previousPrice: 5.9,
      priceEffectiveDate: '2025-01-15',
    },
    dietaryAttributes: [
      { id: 6, source: AllergenSource.Feed, code: 'MILK', name: 'Milk', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.Contains },
      { id: 7, source: AllergenSource.Feed, code: 'VEGETARIAN', name: 'Vegetarian', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable },
    ],
    carbonRating: CarbonRating.E,
    nutrition: {
      source: NutritionSource.Supplier,
      servingSize: '30g',
      per100g: { energyKj: 1700, energyKcal: 410, fat: 34, saturates: 21, carbohydrate: 0.1, sugars: 0.1, fibre: 0, protein: 25, salt: 1.7 },
      perServing: {},
    },
    catalogueAvailability: [
      { catalogueName: 'Core Range', available: true },
      { catalogueName: 'Education Sites', available: false },
    ],
    recipeUsage: [
      { recipeId: 617, recipeName: 'Leek, Potato and Aged Cheddar Soup' },
    ],
    inventoryMapping: [
      { warehouseCode: 'WH-SOUTH', binLocation: 'D-08-2', onHandQty: 55, reorderPoint: 60 },
    ],
  },
  {
    id: 5,
    itemNumber: 'ITM-4410',
    name: 'Mixed Salad Leaves, 1kg',
    category: 'Fresh Produce',
    baseUom: 'KG',
    status: ItemLifecycleStatus.Inactive,
    supplier: {
      supplierName: 'Green Acres Farms',
      supplierCode: 'SUP-390',
      leadTimeDays: 1,
      minimumOrderQuantity: 5,
      alternateSuppliers: ['Harvest Direct'],
    },
    uomConversions: [{ fromUom: 'CASE', toUom: 'KG', factor: 1 }],
    pricing: {
      currency: 'GBP',
      contractPrice: 3.1,
      previousPrice: 3.1,
      priceEffectiveDate: '2024-09-01',
    },
    dietaryAttributes: [
      { id: 8, source: AllergenSource.Feed, code: 'VEGAN', name: 'Vegan', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable },
    ],
    carbonRating: CarbonRating.A,
    nutrition: {
      source: NutritionSource.Supplier,
      servingSize: '100g',
      per100g: { energyKj: 70, energyKcal: 17, fat: 0.2, saturates: 0, carbohydrate: 2, sugars: 1.5, fibre: 1.5, protein: 1.4, salt: 0.02 },
      perServing: {},
    },
    catalogueAvailability: [
      { catalogueName: 'Core Range', available: false },
    ],
    recipeUsage: [],
    inventoryMapping: [
      { warehouseCode: 'WH-SOUTH', binLocation: 'E-01-1', onHandQty: 0, reorderPoint: 40 },
    ],
  },
];
